import { baseURL } from 'app-constants.ts';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RETURN_COOLDOWN_MS = 60 * 1000;
const VERSION_URL = new URL(import.meta.env.VERSION_FILE, baseURL);

// Every check that finds a newer deployed build calls onNewVersion, so a prompt closed with its
// X comes back on the next one: until it reloads, the tab keeps sending queries the Hasura
// allow-list may reject and asking for chunks the deploy removed. The next scheduled check comes
// five minutes after the last check of any kind. Returning to the tab checks right away, at most
// once a minute: focus fires on every switch back from another app. A null buildTs means the dev
// server.
export function watchForNewVersion(
    buildTs: number | null,
    onNewVersion: (deployedBuildTs: number) => void
): () => void {
    if (buildTs === null) return () => {};

    let lastCheckAt = -Infinity;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
        clearTimeout(timer);
        timer = setTimeout(() => void check(), CHECK_INTERVAL_MS);
        if (document.hidden) return;
        lastCheckAt = Date.now();
        try {
            const deployedBuildTs = await fetchDeployedBuildTs();
            // `>` because for a moment after a deploy the CDN can still serve the previous
            // version.json to a tab that already runs the new build.
            if (deployedBuildTs !== null && deployedBuildTs > buildTs) {
                onNewVersion(deployedBuildTs);
            }
        } catch {
            // Offline, or the CDN answered with its index.html fallback.
        }
    };
    const onReturn = () => {
        if (Date.now() - lastCheckAt >= RETURN_COOLDOWN_MS) void check();
    };
    // A page restored from the back/forward cache can get neither visibilitychange nor focus.
    const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) onReturn();
    };

    timer = setTimeout(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onPageShow);

    return () => {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onReturn);
        window.removeEventListener('focus', onReturn);
        window.removeEventListener('pageshow', onPageShow);
    };
}

async function fetchDeployedBuildTs(): Promise<number | null> {
    const response = await fetch(VERSION_URL, { cache: 'no-cache' });
    if (!response.ok) return null;

    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) return null;
    return 'buildTs' in body && typeof body.buildTs === 'number' ? body.buildTs : null;
}
